/**
 * What an analysis's place in the viewer shows before the analysis exists:
 * a pale preview of what will fill it, under a line saying so. The place is
 * kept from the start so the layout never moves when results arrive.
 */
import { ReactNode } from 'react';

// Pale stand-ins for the real content, never mistaken for data.
const GHOST = '#e6e8e2';

/** The note over a ghost preview, centred in the space it is given. */
export function PendingNote({ children }: { children: ReactNode }) {
    return (
        <p className="relative mx-auto max-w-[16rem] rounded-md bg-white/90 px-3 py-1.5 text-center text-xs text-[#7a8077]">
            {children}
        </p>
    );
}

/** The roots curves to come: three soft waves across the band. */
export function GhostCurves({ children }: { children: ReactNode }) {
    return (
        <div className="relative flex h-full items-center justify-center">
            <svg
                className="absolute inset-0 h-full w-full"
                viewBox="0 0 100 30"
                preserveAspectRatio="none"
                aria-hidden
            >
                {[
                    'M0 24 C 15 22, 25 18, 40 16 S 70 9, 100 6',
                    'M0 27 C 20 26, 30 21, 50 20 S 80 14, 100 12',
                    'M0 20 C 10 21, 30 15, 45 17 S 75 10, 100 11',
                ].map((d) => (
                    <path
                        key={d}
                        d={d}
                        fill="none"
                        stroke={GHOST}
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                    />
                ))}
            </svg>
            {children}
        </div>
    );
}

/** The fauna cards to come, or held back during playback: the note if any,
 *  then a few empty tiles with their captions, fading out downwards. */
export function GhostCards({ children }: { children?: ReactNode }) {
    return (
        <div className="space-y-2">
            {children}
            <div
                className="grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-x-2 gap-y-3 [mask-image:linear-gradient(to_bottom,black_30%,transparent)]"
                aria-hidden
            >
                {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className="flex flex-col gap-1.5">
                        <div
                            className="aspect-square w-full rounded-md"
                            style={{ backgroundColor: GHOST }}
                        />
                        <div
                            className="h-2 w-2/3 rounded"
                            style={{ backgroundColor: GHOST }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
